import React, {useEffect, useRef,} from 'react';
import {router, usePage} from '@inertiajs/react';
import {Toast} from "primereact/toast";
import {useAppContext} from "@Providers/AppProvider";


const TOAST_LIFE = 8000;

function normalizeErrorMessages(input: any): string[] {
  if (!input) return [];

  if (typeof input === 'string' || typeof input === 'number') {
    return [String(input)];
  }

  if (Array.isArray(input)) {
    return input.flatMap((v) => normalizeErrorMessages(v));
  }

  if (typeof input === 'object') {
    // handle shapes like { field: "msg" } or { field: { rule: "msg" } }
    return Object.values(input).flatMap((v) => normalizeErrorMessages(v));
  }

  return [];
}

function showErrorToast(toast: React.RefObject<Toast | null>, messages: string[]) {
  toast.current?.show({
    severity: 'error',
    summary: 'Something went wrong',
    detail: (
      <ul className="m-0 p-0 mt-4">
        {messages.map((msg, idx) => (
          <li key={idx} className="mt-2">{msg}</li>
        ))}
      </ul>
    ),
    life: TOAST_LIFE,
  } as any);
}

export default function ToastWrap() {
  const toast = useRef<Toast>(null);
  const lastFlashObj = useRef<{ message?: string } | null>(null);
  const {toastData, showToast} = useAppContext() as any;
  const pageProps = usePage().props as any;

  useEffect(() => {
    const flashError = normalizeErrorMessages(pageProps?.flash?.error);
    const pageError = normalizeErrorMessages(pageProps?.errors?.error);
    const messages = flashError.length ? flashError : pageError;
    if (!messages.length) return;
    setTimeout(() => showErrorToast(toast, messages), 100);
  }, []);

  // Call from context
  useEffect(() => {
    if (!toastData) {
      return;
    }
    const timeout = setTimeout(() => {
      const toastParams = {
        severity: 'info',
        summary: '',
        detail: <b>Something went wrong</b>,
        life: TOAST_LIFE,
      }

      if (toastData.errors && Object.keys(toastData.errors).length > 0) {
        const messages = normalizeErrorMessages(toastData.errors);
        toastParams.severity = 'error';
        toastParams.summary = toastData.summary ?? 'Something went wrong';
        toastParams.detail = (
          <ul className="m-0 p-0 mt-4">
            {messages.map((msg, i) => (
              <li key={i} className="mt-2">{msg}</li>
            ))}
          </ul>
        );
      } else if (toastData.message) {
        toastParams.severity = toastData.severity ?? 'success';
        toastParams.summary = toastData.summary ?? (toastData.severity === 'info' ? 'Info' : 'Success');
        toastParams.detail = (
          <div
            dangerouslySetInnerHTML={{
              __html: toastData.message,
            }}
          />
        );
      }

      toast.current?.show(toastParams as any);
      showToast(null);
    }, 100);
    return () => clearTimeout(timeout);
  }, [toastData, showToast]);

  useEffect(() => {
    return router.on('success', (event) => {
      const flash = (event.detail.page.props as any).flash;
      if (!flash || flash === lastFlashObj.current) return;
      lastFlashObj.current = flash;

      const flashErrors = normalizeErrorMessages(flash.error);
      if (flashErrors.length) {
        showErrorToast(toast, flashErrors);
        return;
      }

      const pageErrors = (event.detail.page.props as any).errors;
      const pageErrorMessages = normalizeErrorMessages(pageErrors?.error);
      if (pageErrorMessages.length) {
        showErrorToast(toast, pageErrorMessages);
        return;
      }

      if (!flash.message) return;
      toast.current?.show({
        severity: 'success',
        summary: 'Success',
        detail: <div dangerouslySetInnerHTML={{ __html: flash.message }} />,
        life: TOAST_LIFE,
      });
    });
  }, []);


  useEffect(() => {
    return router.on('error', (event) => {
      const errorList = normalizeErrorMessages(event.detail.errors);
      if (!errorList.length) return;
      showErrorToast(toast, errorList);
    });
  }, []);

  return  <Toast ref={toast} position="bottom-left" />

}
